import { memo, useEffect, useMemo, useState } from 'react'
import { useTranslations } from '@/core/translation'
import { FilterDropdownPopover, usePrice } from '@/components/Data'
import { PriceRangeFilterDropdownProps } from './PriceRangeFilterDropdown.types'
import PriceRange, { PriceRangeValue } from '../PriceRange'

function PriceRangeFilterDropdown({
  isSelected = false,
  buttonProps = {},
  min,
  max,
  value,
  onChange,
  ...props
}: PriceRangeFilterDropdownProps) {
  const t = useTranslations('filters')

  const [priceRange, setPriceRange] = useState<PriceRangeValue>(value)
  const [minBound] = useState<number>(min)
  const [maxBound] = useState<number>(max)

  const { onClear, ...otherButtonProps } = buttonProps

  const { price: minInputValue } = usePrice({ value: value.min || min || 0 })
  const { price: maxInputValue } = usePrice({ value: value.max || max || 0 })

  const priceRangeLabel = useMemo(() => {
    if (!isSelected) return t('price')
    return `${minInputValue} - ${maxInputValue}`
  }, [isSelected, maxInputValue, minInputValue, t])

  useEffect(() => {
    setPriceRange({ min: value.min, max: value.max })
  }, [value.min, value.max])

  return (
    <FilterDropdownPopover
      label={priceRangeLabel}
      isSelected={isSelected}
      onClear={() => {
        setPriceRange({ min: minBound, max: maxBound })
        onClear?.()
      }}
      buttonProps={otherButtonProps}
      {...props}>
      <PriceRange
        min={minBound}
        max={maxBound}
        value={priceRange}
        onChange={(priceRangeValue) => {
          setPriceRange(priceRangeValue)
          onChange?.(priceRangeValue)
        }}
      />
    </FilterDropdownPopover>
  )
}

export default memo(PriceRangeFilterDropdown)
